import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../../components/common/PageHeader";
import api from "../../utils/api";
import AppsModal from "./components/AppsModal";
import AddAppModal from "./components/AddAppModal";
import EditSaleModal from "./components/EditSaleModal";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";

const OtherAppsSale = () => {
  const navigate = useNavigate();
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAppId, setSavingAppId] = useState(null);
  const [salesForm, setSalesForm] = useState({});

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showAppsModal, setShowAppsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const hasApps = useMemo(() => Array.isArray(apps) && apps.length > 0, [apps]);
  const enabledApps = useMemo(() => apps.filter((app) => app.enabled), [apps]);

  const loadApps = async () => {
    try {
      setLoading(true);
      const res = await api.get("/other-apps");
      setApps(res.data?.apps || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch apps");
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApps();
  }, []);

  const handleOpenApps = async () => {
    await loadApps();
    setShowAppsModal(true);
  };

  const handleAddApp = async (name) => {
    try {
      const res = await api.post("/other-apps", { name });
      const created = res.data?.app;
      toast.success("App saved");
      await loadApps();
      setShowAddModal(false);
      return { success: true, app: created };
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to save app";
      return { success: false, message: msg };
    }
  };

  const handleToggle = async (app) => {
    try {
      setLoading(true);
      await api.patch(`/other-apps/${app._id}/toggle-enabled`);
      await loadApps();
    } catch (e) {
      toast.error("Failed to update app");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (app) => {
    setDeleteTarget({ id: app._id, name: app.name });
  };

  const confirmDeleteApp = async () => {
    if (!deleteTarget) return;
    try {
      setLoading(true);
      await api.delete(`/other-apps/${deleteTarget.id}`);
      toast.success("App deleted");
      setDeleteTarget(null);
      await loadApps();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete app";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    if (!loading) { // Prevent closing while loading
      setDeleteTarget(null);
    }
  };

  const handleSaleFieldChange = (appId, field, value) => {
    setSalesForm((prev) => ({
      ...prev,
      [appId]: {
        ...(prev[appId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveSale = async (app) => {
    const currentForm = salesForm[app._id] || {};
    if (!currentForm.totalSale?.trim()) {
      toast.error("Please enter total sale");
      return;
    }
    if (!currentForm.totalOrders?.trim()) {
      toast.error("Please enter total orders");
      return;
    }
    if (!currentForm.saleDate) {
      toast.error("Please select date");
      return;
    }

    try {
      setSavingAppId(app._id);
      await api.post("/other-apps/daily-sales", {
        appName: app.name,
        totalSale: Number(currentForm.totalSale),
        totalOrders: Number(currentForm.totalOrders),
        saleDate: currentForm.saleDate,
      });
      toast.success(`${app.name} sale saved`);
      setSalesForm((prev) => ({
        ...prev,
        [app._id]: { totalSale: "", totalOrders: "", saleDate: "" },
      }));
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to save sale";
      toast.error(msg);
    } finally {
      setSavingAppId(null);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <PageHeader
        showBackButton={true}
        backTo={-1}
        title="Other Apps Sale"
        subtitle="Manage other apps used for sales"
        action={
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            {/* <button
              onClick={() => setShowEditModal(true)}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 px-5 py-2.5 rounded-xl text-sm font-semibold active:scale-[0.98] transition-all duration-200 w-full md:w-auto"
            >
              Edit Sale
            </button> */}
            <button
              onClick={handleOpenApps}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold active:scale-[0.98] transition-all duration-200 w-full md:w-auto"
            >
              Manage Apps
            </button>
          </div>
        }
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="text-sm font-semibold text-gray-900">Add Daily Sales of Other App</div>
        <div className="text-xs text-gray-500 mt-1">
          {loading ? "Loading..." : hasApps ? `${enabledApps.length} enabled app(s)` : "No app added"}
        </div>

        <div className="mt-5 space-y-3">
          {!enabledApps.length ? (
            <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl p-4">
              No enabled app available.
            </div>
          ) : (
            enabledApps.map((app) => (
              <div
                key={app._id}
                className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_minmax(0,200px)] gap-3 items-end border border-gray-100 rounded-xl p-3"
              >
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                    App Name
                  </label>
                  <div className="h-[46px] flex items-center text-base font-bold text-gray-900 px-1">
                    {app.name}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                    Total Sale
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-gray-500 font-medium select-none pointer-events-none">
                      {currencySymbol}
                    </span>
                    <input
                      type="text"
                      value={salesForm[app._id]?.totalSale || ""}
                      onChange={(e) =>
                        handleSaleFieldChange(app._id, "totalSale", e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="Eg: 1200"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
                      inputMode="numeric"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                    Total Orders
                  </label>
                  <input
                    type="text"
                    value={salesForm[app._id]?.totalOrders || ""}
                    onChange={(e) =>
                      handleSaleFieldChange(app._id, "totalOrders", e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="Eg: 50"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={salesForm[app._id]?.saleDate || ""}
                    onChange={(e) => handleSaleFieldChange(app._id, "saleDate", e.target.value)}
                    max={today}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-transparent uppercase tracking-widest mb-2 select-none pointer-events-none">
                    Action
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 w-full min-h-[46px]">
                    <button
                      type="button"
                      onClick={() => handleSaveSale(app)}
                      disabled={savingAppId === app._id}
                      className={`flex-1 h-[46px] rounded-xl text-sm font-semibold transition-all duration-200 ${savingAppId === app._id
                          ? "bg-gray-100 text-gray-400 cursor-wait"
                          : "bg-blue-600 hover:bg-blue-700 text-white active:scale-[0.98]"
                        }`}
                    >
                      {savingAppId === app._id ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/other-apps-sale/app/${app._id}`, { state: { app } })
                      }
                      className="flex-1 h-[46px] rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 transition-all duration-200 active:scale-[0.98]"
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <AppsModal
        open={showAppsModal}
        onClose={() => setShowAppsModal(false)}
        apps={apps}
        onAddClick={() => setShowAddModal(true)}
        onToggle={handleToggle}
        onDelete={handleDelete}
        loading={loading}
      />

      <AddAppModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddApp}
      />

      <EditSaleModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/60 border-0 cursor-default"
            onClick={cancelDelete}
            aria-label="Close dialog backdrop"
          />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden p-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Do you want to delete this app?</h3>
            <p className="text-sm text-gray-500 mb-8">
              Are you sure you want to delete the application "<span className="font-semibold text-gray-900">{deleteTarget.name}</span>"? 
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={cancelDelete}
                className="flex-1 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold rounded-xl border border-gray-200"
              >
                Cancel
              </button>
              <button type="button" onClick={confirmDeleteApp} disabled={loading} className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl">
                {loading ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherAppsSale;
